import { registerBrick, BrickContext } from 'olympe';
import { ReactBrick, useProperty, getLogger } from '@olympeio/core';
// prettier-ignore
import { Typography, TextField, Button, DialogTitle, DialogContent, DialogActions, ThemeProvider, IconButton, FormControlLabel, Checkbox } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import React from 'react';
import { globalTheme } from '../shared/_theme.jsx';
import { MasterDataAutoComplete } from '../shared/MasterDataAutoComplete.jsx';
import { parse, format, isBefore } from 'date-fns';
import './blocked_parked.css';

export default class EditApproverForResponsibilityForm extends ReactBrick {
    /**
     * @override
     * @protected
     * @param {!BrickContext} $
     * @param {!Array<*>} properties
     * @returns {Element}
     */

    static getReactComponent($) {
        const getOptions = $.get('get options');
        const DATE_FORMAT = 'yyyy-MM-dd';
        return (props) => {
            const logger = getLogger('EditApproverForResponsibilityForm');
            const type = useProperty($, 'type');
            const currentAgentId = useProperty($, 'current agent id');
            const newAgentId = useProperty($, 'new agent id');
            const [selectedAgentOption, setSelectedAgentOption] = React.useState(null);
            const startDate = useProperty($, 'start date');
            const endDate = useProperty($, 'end date');
            const isMassChange = useProperty($, 'is mass change');
            const isMassChangeBatch = useProperty($, 'is mass change batch');
            const [isValid, setIsValid] = React.useState(false);
            const [isUser, setIsUser] = React.useState(false);

            React.useEffect(() => {
                setIsUser(type === 'US');
            }, [type]);

            React.useEffect(() => {
                setIsValid(!!newAgentId && !!startDate && !!endDate && isBefore(startDate, endDate));
            }, [newAgentId, startDate, endDate]);

            // Update prop back to Draw
            const updateProp = (prop, value) => {
                $.set(prop, value);
            };
            const handleAgentChange = (newAgentOption, propName) => {
                setSelectedAgentOption(newAgentOption);
                updateProp('new agent id', newAgentOption?.[propName] ?? '');
            };

            // Buttons handlers
            const handleCancel = () => {
                $.trigger('On Cancel');
            };
            const handleSave = () => {
                $.trigger('On Save');
            };

            return (
                <>
                    <ThemeProvider theme={globalTheme}>
                        <DialogTitle>
                            Edit Agent
                            <IconButton
                                aria-label="close"
                                onClick={handleCancel}
                                sx={{
                                    position: 'absolute',
                                    right: 10,
                                    top: 10,
                                    color: (theme) => theme.palette.primary.contrastText,
                                }}
                            >
                                <CloseIcon />
                            </IconButton>
                        </DialogTitle>
                        <DialogContent style={{ padding: '32px 16px' }}>
                            <div className="edit-agent-responsibility-form">
                                <div>
                                    <Typography variant="caption">Type</Typography>
                                    <Typography>{isUser ? 'User' : 'Position'}</Typography>
                                </div>
                                <div>
                                    <Typography variant="caption">Current Agent Id</Typography>
                                    <Typography>{currentAgentId}</Typography>
                                </div>
                                {isUser ? (
                                    <div>
                                        <Typography variant="caption">New User Id</Typography>
                                        <MasterDataAutoComplete
                                            context={$}
                                            logger={logger}
                                            getOptions={getOptions}
                                            path={'ApproverSet'}
                                            filter={(value) => `IdNo eq 'B1' and SearchTerm eq '${value}'`}
                                            value={selectedAgentOption}
                                            getOptionLabel={(option) =>
                                                option ? `${option.ApproverName} (${option.ApproverId})` : ''
                                            }
                                            isOptionEqualToValue={(option, value) => option.ApproverId === value}
                                            onChange={(value) => handleAgentChange(value, 'ApproverId')}
                                        />
                                    </div>
                                ) : null}
                                {!isUser ? (
                                    <div>
                                        <Typography variant="caption">New Position Id</Typography>
                                        <MasterDataAutoComplete
                                            context={$}
                                            logger={logger}
                                            getOptions={getOptions}
                                            path={'PositionIdSet'}
                                            filter={(value) => `IdNo eq '01' and SearchTerm eq '${value}'`}
                                            value={selectedAgentOption}
                                            getOptionLabel={(option) =>
                                                option ? `${option.PosIdName} (${option.PosId})` : ''
                                            }
                                            isOptionEqualToValue={(option, value) => option.PosId === value}
                                            onChange={(value) => handleAgentChange(value, 'PosId')}
                                        />
                                    </div>
                                ) : null}
                                <div></div>
                                <div>
                                    <Typography variant="caption">Start Date</Typography>
                                    <TextField
                                        type="date"
                                        value={startDate ? format(startDate, DATE_FORMAT) : ''}
                                        onChange={(event) =>
                                            updateProp('start date', parse(event.target.value, DATE_FORMAT, Date.now()))
                                        }
                                        fullWidth
                                    ></TextField>
                                </div>
                                <div>
                                    <Typography variant="caption">End Date</Typography>
                                    <TextField
                                        type="date"
                                        value={endDate ? format(endDate, DATE_FORMAT) : ''}
                                        onChange={(event) =>
                                            updateProp('end date', parse(event.target.value, DATE_FORMAT, Date.now()))
                                        }
                                        InputProps={{ inputProps: { min: startDate } }}
                                        fullWidth
                                    ></TextField>
                                </div>
                                <div>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                onChange={(e) => {
                                                    $.set('is mass change', e.target.checked);
                                                }}
                                                checked={isMassChange ?? false}
                                            />
                                        }
                                        label="Is Mass Change ?"
                                    />
                                </div>
                                {isMassChange && (
                                    <div>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    onChange={(e) => {
                                                        $.set('is mass change batch', e.target.checked);
                                                    }}
                                                    checked={isMassChangeBatch ?? false}
                                                />
                                            }
                                            label="Batch (Background processing)"
                                        />
                                    </div>
                                )}
                            </div>
                        </DialogContent>
                        <DialogActions>
                            <Button variant="text" onClick={handleCancel}>
                                Cancel
                            </Button>
                            <Button variant="contained" disabled={!isValid} onClick={handleSave}>
                                Save
                            </Button>
                        </DialogActions>
                    </ThemeProvider>
                </>
            );
        };
    }
}

registerBrick('018b136abae47c3ebfbf', EditApproverForResponsibilityForm);
